import React, { useState } from 'react';
import axios from 'axios';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create WhatsApp message with contact form data
    const message = `*Contact Form Submission*%0A%0AName: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0ASubject: ${formData.subject}%0AMessage: ${formData.message}`;
    
    // Redirect to WhatsApp
    window.open(`https://wa.me/919448650161?text=${message}`, '_blank');
    
    // Clear form
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-[#2E368F] text-white py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">Contact Us</h1>
          <p className="text-xl text-white max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#F1F8E9]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-8 text-[#1B3322]">Get in Touch</h2>
              <p className="text-lg text-[#4E5D50] mb-8 leading-relaxed">
                Whether you have a question about our products, pricing, installation, or anything else, our team is ready to answer all your questions.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-[#6ABD45] rounded-full flex items-center justify-center flex-shrink-0">
                    <FaPhone className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#2E368F] mb-1">Phone</h3>
                    <a href="tel:+919448650161" className="text-gray-600 hover:text-[#6ABD45] transition-colors">
                      +91 9448650161
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-whatsapp">
                  <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                    <FaWhatsapp className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#2E368F] mb-1">WhatsApp</h3>
                    <a
                      href="https://wa.me/919448650161"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#25D366] transition-colors"
                    >
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#29AAE2] rounded-full flex items-center justify-center flex-shrink-0">
                    <FaEnvelope className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#2E368F] mb-1">Email</h3>
                    <a href="mailto:info@gangairrigation.com" className="text-gray-600 hover:text-[#29AAE2] transition-colors">
                      info@gangairrigation.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-address">
                  <div className="w-12 h-12 bg-[#2E368F] rounded-full flex items-center justify-center flex-shrink-0">
                    <FaMapMarkerAlt className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#2E368F] mb-1">Address</h3>
                    <p className="text-gray-600 leading-relaxed">
                      GANGA IRRIGATION<br />
                      Plot No 233, Nandur Kesaratagi Industrial Area<br />
                      Kalaburagi, Kalaburagi<br />
                      Kalaburagi, KARNATAKA 585105<br />
                      India
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-12 bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-bold text-xl text-[#1B3322] mb-4">Business Hours</h3>
                <div className="space-y-2 text-[#4E5D50]">
                  <div className="flex justify-between">
                    <span>Monday - Saturday:</span>
                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-semibold">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h2 className="text-3xl font-bold mb-6 text-[#1B3322]">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-semibold text-[#1B3322] mb-2">Name *</label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="w-full"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1B3322] mb-2">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="w-full"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1B3322] mb-2">Phone *</label>
                  <Input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 XXXXXXXXXX"
                    className="w-full"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1B3322] mb-2">Subject *</label>
                  <Input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="How can we help you?"
                    className="w-full"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1B3322] mb-2">Message *</label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us more about your requirements..."
                    rows={5}
                    className="w-full"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-full py-6 text-lg font-semibold"
                  data-testid="submit-contact-button"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;